import React, { useState, useEffect } from "react"
import * as Sentry from "@sentry/browser"
import { RouterOutlined, CodeOutlined, Security, HourglassEmptyOutlined } from "@material-ui/icons"
import { Snackbar, Chip, makeStyles } from "@material-ui/core"
import Config from "react-global-configuration"
import { Icon } from "../Icon"
import { useSnackbarChipStyles } from "../SnackbarProvider"

const useSnackbarStyles = makeStyles(() => ({
  root: {
    opacity: 0.9,
  },
}))

const getErrorsUI = (error) => {
  const { graphQLErrors, networkError } = error

  if (graphQLErrors) {
    const codes = graphQLErrors.map((error) => error.extensions?.code).filter((code) => code)

    graphQLErrors.forEach((error) => console.log("[ErrorsUI]", error.message))

    if (codes.includes("FORBIDDEN") || codes.includes("PERMISSION_DENIED")) {
      return {
        icon: <Security />,
        label: "Permission was denied for request",
        color: "secondary",
        autoHideDuration: 5000,
      }
    }

    if (codes.includes("OPERATION_DENIED")) {
      return {
        icon: <Icon name="delete-failed" />,
        label: graphQLErrors.find((error) => error.extensions?.code === "OPERATION_DENIED")?.message,
        color: "secondary",
        autoHideDuration: 5000,
      }
    }

    if (codes.includes("NOT_FOUND")) {
      return {
        icon: <Icon name="not-found" />,
        label: graphQLErrors.find((error) => error.extensions?.code === "NOT_FOUND")?.message,
        color: "primary",
        autoHideDuration: 5000,
      }
    }

    if (codes.includes("RESOURCE_LOCKED")) {
      return {
        icon: <HourglassEmptyOutlined />,
        label: "Resource locked by another process/user, try again",
        color: "secondary",
        autoHideDuration: 5000,
      }
    }

    if (codes.includes("BAD_GATEWAY")) {
      return {
        icon: <Icon name="integration" />,
        label: graphQLErrors.find((error) => error.extensions?.code === "BAD_GATEWAY")?.message,
        color: "secondary",
        autoHideDuration: 5000,
      }
    }

    if (codes.includes("BAD_USER_INPUT")) {
      return { ignore: true }
    }

    return {
      icon: <CodeOutlined />,
      label: "Problem occurred requesting a resource",
      color: "secondary",
      autoHideDuration: null,
    }
  }

  if (networkError?.statusCode === 429) {
    Sentry.captureMessage("User was rate limited")
    return {
      icon: <Icon name="rate-limit" />,
      label: "You've reached our rate limit, please try again",
      color: "primary",
      autoHideDuration: 2000,
    }
  }

  return {
    icon: <RouterOutlined />,
    label: "No or low connectivity",
    color: "primary",
    autoHideDuration: 2000,
  }
}

const ConnectivityError = ({ server }) => {
  const chipClasses = useSnackbarChipStyles()
  const snackbarClasses = useSnackbarStyles()
  const [error, setError] = useState(false)
  const { showGraphQLErrorNotification } = Config.get("errors")

  const handleClose = () => {
    setError(null)
  }

  useEffect(() => {
    server.registerErrorHandler("ConnectivityError", (errorInfo) => {
      const { graphQLErrors, networkError } = errorInfo
      if (Array.isArray(graphQLErrors)) {
        for (const err of graphQLErrors) {
          switch (err.extensions.code) {
            case "UNAUTHENTICATED":
              return // ignore
            default:
              if (showGraphQLErrorNotification) setError(errorInfo)
          }
        }
      }

      if (networkError) {
        console.log("[NETWORK]", `[ERROR]: ${networkError}`)
        setError(errorInfo)
      }
    })
    return () => {
      server.removeErrorHandler("ConnectivityError")
    }
  }, [server, showGraphQLErrorNotification])

  if (!error) return null

  const { icon, label, color, autoHideDuration, ignore } = getErrorsUI(error)

  if (ignore) return null

  return (
    <Snackbar
      classes={snackbarClasses}
      open={error != null}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Chip icon={icon} label={label} color={color} classes={chipClasses} />
    </Snackbar>
  )
}

export default ConnectivityError
